import { CompRef } from '@wix/document-services-types';
import {
  WidgetSlot,
  WidgetSlotPosition,
  WidgetSlotToMoveContent,
  PluginActionContext,
  UniversalSlotPointer,
} from '@wix/editor-platform-host-integration-apis';
import { extendPluginActionContext } from './utils';

import { PlatformContext } from '../../../types/platformApi';
import { PluginHostApi } from './types';
import { SlotPointer } from './slotPointer';

export class SlotsService {
  hostAPI: PluginHostApi;

  constructor(hostAPI: PluginHostApi) {
    this.hostAPI = hostAPI;
  }

  /**
   * @deprecated Will be deprecated in upcoming PRs — use
   * `getUniversalSlotPointerByPluginCompRef` instead.
   */
  public getSlotPlaceholderRefByContentRef(
    compRef: CompRef,
  ): UniversalSlotPointer | CompRef | undefined {
    return this.hostAPI.getSlotPlaceholderRefByContentRef(compRef);
  }

  public getUniversalSlotPointerByPluginCompRef(
    compRef: CompRef,
  ): UniversalSlotPointer | undefined {
    return this.hostAPI.getUniversalSlotPointerByPluginCompRef(compRef);
  }

  public getWidgetHostRefBySlotRef(compRef: CompRef): CompRef {
    return this.hostAPI.getWidgetHostRefBySlotRef(compRef);
  }

  public getWidgetHostRefByWidgetRef(compRef: CompRef): CompRef {
    return this.hostAPI.getWidgetHostRefByWidgetRef(compRef);
  }

  public getWidgetSlot(
    slotPointer: SlotPointer,
    options: { includePrivate: boolean },
  ): WidgetSlot {
    return this.hostAPI.getWidgetSlot(slotPointer, options);
  }

  public isSlotPublic(slotPointer: SlotPointer): boolean {
    return this.hostAPI.isSlotPublic(slotPointer);
  }

  public getWidgetSlots(
    widgetCompRef: CompRef,
    options: { includePrivate: boolean },
  ): WidgetSlot[] {
    return this.hostAPI.getWidgetSlots(widgetCompRef, options);
  }

  public getSlotPosition(compRef: CompRef): WidgetSlotPosition | null {
    return this.hostAPI.getSlotPosition(compRef);
  }

  public getCompRefForSlotSelection(
    slotPointer: SlotPointer,
  ): CompRef | undefined {
    return this.hostAPI.getCompRefForSlotSelection(slotPointer);
  }

  public getSlotInBlocksAppEditor(compRef: CompRef): WidgetSlot | undefined {
    return this.hostAPI.getSlotInBlocksAppEditor(compRef);
  }

  public getWidgetSlotsToMoveContent(
    slotPointer: SlotPointer,
  ): WidgetSlotToMoveContent[] {
    return this.hostAPI.getWidgetSlotsToMoveContent(slotPointer);
  }

  public getNearestWidgetHostRef(compRef: CompRef): CompRef | undefined {
    return this.hostAPI.getNearestWidgetHostRef(compRef);
  }

  public hasWidgetSlot(slotComponentRef: CompRef): boolean {
    return this.hostAPI.hasWidgetSlot(slotComponentRef);
  }

  public async moveToSlot(
    context: PlatformContext,
    fromSlot: SlotPointer,
    toSlot: SlotPointer,
    pluginActionContext: PluginActionContext,
  ): Promise<void> {
    const actionContext = extendPluginActionContext(pluginActionContext);
    return this.hostAPI.moveToSlot(context, fromSlot, toSlot, actionContext);
  }

  public async highlightSlot(slotPointer: SlotPointer): Promise<void> {
    return this.hostAPI.highlightSlot(slotPointer);
  }

  public unhighlightSlot(slotPointer: SlotPointer): void {
    return this.hostAPI.unhighlightSlot(slotPointer);
  }

  public isSlotContentPotentialMovable(slotPointer: SlotPointer): boolean {
    return this.hostAPI.isSlotContentPotentialMovable(slotPointer);
  }
}
